// Checks dashboard (F05): the system checks as `check.history` publishes them — families and each check's latest result with
// its trend across runs, the runs with their counts, schedules (an overdue schedule is shown), and the FAILs of a run with the
// engine's reasons (`check.show`, asked through the inbox). A check that cannot run is a FAIL, never a pass. Double checks of
// sampled acts come from each engagement's published samples.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../../app/context";
import { Card, DataTable, PageHeader, StatusPill } from "../../components";
import type { DoubleCheck } from "../../data/types";
import { fmtStamp } from "../../lib/format";
import { useLoad } from "../../lib/hooks";
import { ActButton, Computed, ModelGate, ReadView, Tiles, useActs, useEngineRead, useFirmModels } from "./common";

type Doc = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
const WORD: Record<string, string> = { pass: "passed", fail: "failed", warn: "warned" };
const word = (s: string | undefined) => (s ? WORD[s] ?? s : "not_started");

export function ChecksPage() {
  const ms = useFirmModels(["checks"]);
  const [act, drawer] = useActs();
  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <PageHeader title="Checks" subtitle="System checks: families, latest results, trends and failures with the engine's reasons. A check that cannot run is a failure, never a pass."
        actions={<ActButton command="check.run" label="Run checks…" tone="primary" onClick={() => act({ command: "check.run", title: "Run system checks" })} />} />
      {ms.error && <p role="alert" className="text-sm text-blocked">{ms.error}</p>}
      {ms.data && (
        <ModelGate<Doc> kind="checks" m={ms.data.checks} label="System checks">
          {(d, m) => <ChecksBody d={d} computed={<Computed m={m} />} />}
        </ModelGate>
      )}
      <DoubleChecks />
      {drawer}
    </div>
  );
}

function ChecksBody({ d, computed }: { d: Doc; computed: React.ReactNode }) {
  const runs: Doc[] = d.runs ?? [];
  const history: Record<string, Doc[]> = d.checks ?? {};
  const catalogue: Doc[] = d.catalogue ?? [];
  const families = [...new Set(catalogue.map((c) => String(c.family)))];
  const latest = runs[0];
  const [run, setRun] = useState<number | null>(latest?.id ?? null);
  const failing = Object.entries(history).filter(([, h]) => h[h.length - 1]?.status === "fail").map(([k]) => k);
  const overdue = (d.schedules ?? []).filter((s: Doc) => s.overdue);
  return (
    <>
      <Tiles items={[
        { label: "Latest run", value: latest ? `#${latest.id}` : "none", tone: latest ? undefined : "waiting" },
        { label: "Passed", value: latest?.passed ?? 0, tone: "done" },
        { label: "Warned", value: latest?.warned ?? 0, tone: latest?.warned ? "waiting" : undefined },
        { label: "Failed", value: latest?.failed ?? 0, tone: latest?.failed ? "blocked" : undefined },
        { label: "Checks failing now", value: failing.length, tone: failing.length ? "blocked" : "done" },
        { label: "Schedules overdue", value: overdue.length, tone: overdue.length ? "blocked" : "done" },
      ]} />
      {overdue.length > 0 && (
        <p role="note" className="rounded-md border border-blocked-line bg-blocked-bg px-3 py-2 text-sm text-blocked">
          Overdue: {overdue.map((s: Doc) => `the ${s.schedule} run (every ${s.every_hours} h; last ${s.last_at ? fmtStamp(s.last_at) : "never"})`).join("; ")}.
        </p>
      )}
      <Card title="Families and latest results" action={computed}>
        <div className="grid gap-3 md:grid-cols-2">
          {families.map((f) => (
            <section key={f} aria-label={`Family ${f}`} className="rounded-md border border-line p-2">
              <h3 className="mb-1 text-sm font-semibold capitalize">{f}</h3>
              <ul className="divide-y divide-line text-sm">
                {catalogue.filter((c) => c.family === f).map((c) => {
                  const h = history[c.check] ?? [];
                  const last = h[h.length - 1];
                  return (
                    <li key={c.check} className="flex flex-wrap items-center gap-2 py-1.5">
                      <StatusPill state={word(last?.status)} label={last ? undefined : "Not run"} />
                      <span className="min-w-0 flex-1"><span className="font-mono text-xs">{c.check}</span><span className="block text-xs text-muted">{c.method}</span></span>
                      <span aria-label={`Trend of ${c.check}: ${h.map((x) => x.status).join(", ") || "no runs"}`} className="flex gap-0.5">
                        {h.slice(-12).map((x, i) => <span key={i} title={`run ${x.run}: ${x.status}`} className={`inline-block h-3 w-2 rounded-sm ${x.status === "pass" ? "bg-done" : x.status === "fail" ? "bg-blocked" : "bg-waiting"}`} />)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </Card>
      <Card title="Runs">
        {runs.length === 0 ? <p className="text-sm text-muted">No run yet. Run the checks to start the history.</p> : (
          <ul className="divide-y divide-line text-sm">
            {runs.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2 py-2">
                <StatusPill state={r.failed ? "failed" : r.warned ? "warned" : "passed"} />
                <span className="min-w-0 flex-1">Run #{r.id} · {r.schedule} · {(r.families ?? []).join(", ")} · {r.passed} passed, {r.warned} warned, {r.failed} failed
                  <span className="block text-xs text-muted">{r.by}{r.agent ? ` (agent ${r.agent})` : ""} · {fmtStamp(r.started_at)} · report <span className="font-mono">{String(r.report_sha ?? "").slice(0, 12)}</span></span></span>
                <button type="button" className="text-xs text-brand hover:underline" aria-pressed={run === r.id} onClick={() => setRun(r.id)}>Results</button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {run !== null && <RunResults run={run} />}
    </>
  );
}

function RunResults({ run }: { run: number }) {
  const r = useEngineRead<Doc>("check.show", { run }, `the results of run ${run}`);
  return (
    <Card title={`Results of run #${run}`}>
      <ReadView r={r} label={`the results of run ${run}`}>
        {(d) => {
          const results: Doc[] = d.results ?? [];
          const fails = results.filter((x) => x.status === "fail");
          return (
            <div className="space-y-3 text-sm">
              <p className="text-xs text-muted">{d.tool} · report {d.report_intact ? "intact" : <span className="text-blocked">NOT intact — the report file does not match its recorded hash</span>}</p>
              {fails.length > 0 && (
                <section aria-label="Failures">
                  <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-blocked">Failures ({fails.length})</h3>
                  {fails.map((f) => (
                    <div key={f.check_id} className="mb-2 rounded-md border border-blocked-line bg-blocked-bg p-2">
                      <p className="font-mono text-xs">{f.check_id}</p>
                      <ul className="list-inside list-disc">{(f.problems ?? []).map((p: string) => <li key={p}>{p}</li>)}</ul>
                      <p className="text-xs text-muted">How: {f.method}</p>
                    </div>
                  ))}
                </section>
              )}
              <ul className="divide-y divide-line">
                {results.filter((x) => x.status !== "fail").map((x) => (
                  <li key={x.check_id} className="flex flex-wrap items-center gap-2 py-1"><StatusPill state={word(x.status)} /><span className="font-mono text-xs">{x.check_id}</span><span className="text-xs text-muted">{x.seconds}s</span></li>
                ))}
              </ul>
            </div>
          );
        }}
      </ReadView>
    </Card>
  );
}

function DoubleChecks() {
  const { ds } = useApp();
  const rows = useLoad(async () => {
    const es = await ds.engagements();
    const per = await Promise.all(es.map(async (e) => (await ds.table(e.id, "double_checks").catch(() => [] as DoubleCheck[])).map((x) => ({ ...x, client: e.clientName }))));
    return per.flat();
  }, [ds]);
  return (
    <Card title="Double checks of sampled acts" bodyClass="p-3">
      <DataTable rows={rows.data} loading={rows.loading} error={rows.error} onRetry={rows.reload} rowKey={(r) => `${r.engagementId}:${r.id}`} caption="Double checks" dense
        empty="No act is sampled for a double check on your engagements." filter={{ id: "verdict", label: "verdict", value: (r) => r.verdict ?? "waiting" }}
        columns={[
          { id: "client", header: "Engagement", cell: (r) => <Link className="hover:underline" to={`/e/${r.engagementId}/details/health`}>{r.client}</Link> },
          { id: "cmd", header: "Sampled act", cell: (r) => <span className="font-mono text-xs">{r.command} (audit #{r.auditSeq})</span> },
          { id: "verifier", header: "Verifier", cell: (r) => r.verifier },
          { id: "verdict", header: "Verdict", cell: (r) => <StatusPill state={r.verdict === "agree" ? "done" : r.verdict === "disagree" ? "disputed" : "waiting"} label={r.verdict ?? "Waiting for the verifier"} /> },
          { id: "outcome", header: "Outcome", cell: (r) => <span className="text-xs">{r.outcome ?? "—"}</span> },
        ]} />
    </Card>
  );
}
